import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { X, Check, UserPlus } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import '../styles/EmlakSunum.css';
import TrDateInput from '../components/TrDateInput';

interface SunumPortfoy {
  sunumPortfoyID: number;
  portfoyID: number;
  portfoyBaslik: string;
  portfoyFiyat: number;
  portfoyIlanNo: string;
  vitrinFoto: string | null;
  durum: string;
  musteriDurum: string;
}

interface EmlakSunumListItem {
  sunumID: number;
  takipNo: string;
  cariID: number;
  cariAdSoyad: string;
  cariFirma: string | null;
  cariTelefon: string | null;
  olusturanKullaniciID: number;
  olusturanAdSoyad: string;
  baslik: string | null;
  aciklama: string | null;
  durum: string;
  olusturmaTarihi: string;
  guncellemeTarihi: string | null;
  aktifMi: boolean;
  portfoyAdet: number;
  portfoyler: SunumPortfoy[];
}

interface Cari {
  cariID: number;
  adiSoyadi: string;
  firmaAdi: string | null;
  telefon1: string;
  ePosta: string | null;
  turu: string;
}

interface EmlakPortfoyItem {
  portfoyID: number;
  baslik: string;
  fiyat: number;
  m2: number;
  ilanNo: string;
  tur: string;
  durum: string;
  aktifMi: boolean;
  adres: string | null;
  fotograflar: { fotoID: number; dosyaYolu: string; isVitrin: boolean }[];
}

const sunumDurumLabels: Record<string, string> = {
  'Taslak': 'Taslak',
  'Devam Ediyor': 'Devam Ediyor',
  'Tamamlandı': 'Tamamlandı',
  'Olumlu Sonuçlandı': 'Olumlu Sonuçlandı',
  'Olumsuz Sonuçlandı': 'Olumsuz Sonuçlandı',
  'İptal Edildi': 'İptal Edildi'
};

const sunumDurumBadge: Record<string, { bg: string; color: string }> = {
  'Taslak':             { bg: '#f1f5f9', color: '#475569' },
  'Devam Ediyor':       { bg: '#eff6ff', color: '#1d4ed8' },
  'Tamamlandı':         { bg: '#f0fdf4', color: '#15803d' },
  'Olumlu Sonuçlandı':  { bg: '#f0fdf4', color: '#15803d' },
  'Olumsuz Sonuçlandı': { bg: '#fff1f2', color: '#be123c' },
  'İptal Edildi':       { bg: '#fff1f2', color: '#be123c' },
};
const sunumDurumBarColor: Record<string, string> = {
  'Taslak': '#94a3b8',
  'Devam Ediyor': '#3b82f6',
  'Tamamlandı': '#10b981',
  'Olumlu Sonuçlandı': '#059669',
  'Olumsuz Sonuçlandı': '#ef4444',
  'İptal Edildi': '#6b7280'
};

const EmlakSunumlari: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sunumlar, setSunumlar] = useState<EmlakSunumListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterDurumlar, setFilterDurumlar] = useState<string[]>(['Taslak', 'Devam Ediyor']);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showHizliMusteriModal, setShowHizliMusteriModal] = useState(false);
  const [hizliMusteriSaving, setHizliMusteriSaving] = useState(false);
  const [hizliMusteriForm, setHizliMusteriForm] = useState({
    adiSoyadi: '', firmaAdi: '', telefon1: '', telefon2: '', ePosta: '', adres: '', tc: '', cariVD: '', cariVN: '', turu2: '', aktifMi: true
  });
  const [hizliMusteriAltKategoriler, setHizliMusteriAltKategoriler] = useState<{cariAltKategoriID: number; altKategoriAdi: string}[]>([]);
  const [filterTarih, setFilterTarih] = useState('');

  // Oluşturma modal state'leri
  const [cariler, setCariler] = useState<Cari[]>([]);
  const [portfoyler, setPortfoyler] = useState<EmlakPortfoyItem[]>([]);
  const [cariSearch, setCariSearch] = useState('');
  const [selectedCari, setSelectedCari] = useState<Cari | null>(null);
  const [portfoySearch, setPortfoySearch] = useState('');
  const [selectedPortfoyler, setSelectedPortfoyler] = useState<number[]>([]);
  const [sunumBaslik, setSunumBaslik] = useState('');
  const [sunumAciklama, setSunumAciklama] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchSunumlar();
  }, []);

  const fetchSunumlar = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/EmlakSunumlari');
      setSunumlar(response.data);
    } catch (error) {
      toast.error('Sunumlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCariler = async () => {
    try {
      const response = await axiosInstance.get('/api/Cariler?tur=M%C3%BC%C5%9Fteri');
      setCariler(response.data);
    } catch (error) {
      toast.error('Müşteriler yüklenemedi.');
    }
  };

  const fetchHizliMusteriAltKategoriler = async () => {
    try {
      const res = await axiosInstance.get('/api/CariAltKategorileri/aktif?turu=M%C3%BC%C5%9Fteri');
      setHizliMusteriAltKategoriler(res.data);
    } catch { /* sessiz */ }
  };

  const handleOpenHizliMusteri = () => {
    setHizliMusteriForm({ adiSoyadi: '', firmaAdi: '', telefon1: '', telefon2: '', ePosta: '', adres: '', tc: '', cariVD: '', cariVN: '', turu2: '', aktifMi: true });
    fetchHizliMusteriAltKategoriler();
    setShowHizliMusteriModal(true);
  };

  const handleHizliMusteriSave = async () => {
    if (!hizliMusteriForm.adiSoyadi.trim()) { toast.warning('Ad Soyad zorunludur.'); return; }
    if (!hizliMusteriForm.telefon1.trim()) { toast.warning('Telefon zorunludur.'); return; }
    setHizliMusteriSaving(true);
    try {
      const payload = {
        adiSoyadi: hizliMusteriForm.adiSoyadi,
        firmaAdi: hizliMusteriForm.firmaAdi || null,
        telefon1: hizliMusteriForm.telefon1.replace(/\D/g, '').slice(0, 10),
        telefon2: hizliMusteriForm.telefon2 ? hizliMusteriForm.telefon2.replace(/\D/g, '').slice(0, 10) : null,
        ePosta: hizliMusteriForm.ePosta || null,
        adres: hizliMusteriForm.adres || null,
        tc: hizliMusteriForm.tc || null,
        cariVD: hizliMusteriForm.cariVD || null,
        cariVN: hizliMusteriForm.cariVN || null,
        turu: 'M\u00fc\u015fteri',
        turu2: hizliMusteriForm.turu2 || null,
        aktifMi: hizliMusteriForm.aktifMi
      };
      const res = await axiosInstance.post('/api/Cariler', payload);
      toast.success('M\u00fc\u015fteri ba\u015far\u0131yla eklendi.');
      await fetchCariler();
      const yeniCari = res.data as Cari;
      setSelectedCari(yeniCari);
      setCariSearch('');
      setShowHizliMusteriModal(false);
    } catch {
      toast.error('M\u00fc\u015fteri eklenirken hata olu\u015ftu.');
    } finally {
      setHizliMusteriSaving(false);
    }
  };

  const fetchPortfoyler = async () => {
    try {
      const response = await axiosInstance.get('/api/EmlakPortfoy?aktifMi=true');
      setPortfoyler(response.data);
    } catch (error) {
      toast.error('Portföyler yüklenemedi.');
    }
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setSelectedCari(null);
    setSelectedPortfoyler([]);
    setSunumBaslik('');
    setSunumAciklama('');
    setCariSearch('');
    setPortfoySearch('');
    fetchCariler();
    fetchPortfoyler();
  };

  const handleCreate = async () => {
    if (!selectedCari) {
      toast.warning('Lütfen bir müşteri seçin.');
      return;
    }
    if (selectedPortfoyler.length === 0) {
      toast.warning('Lütfen en az bir portföy seçin.');
      return;
    }

    try {
      setCreating(true);
      const response = await axiosInstance.post('/api/EmlakSunumlari', {
        cariID: selectedCari.cariID,
        olusturanKullaniciID: user?.kullaniciID,
        baslik: sunumBaslik || null,
        aciklama: sunumAciklama || null,
        portfoyIDler: selectedPortfoyler
      });
      toast.success('Sunum oluşturuldu.');
      setShowCreateModal(false);
      navigate(`/emlak-sunumlari/${response.data.sunumID}`);
    } catch (error) {
      toast.error('Sunum oluşturulurken bir hata oluştu.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axiosInstance.delete(`/api/EmlakSunumlari/${id}`);
      toast.success('Sunum silindi.');
      setDeleteConfirm(null);
      fetchSunumlar();
    } catch (error) {
      toast.error('Silme işlemi sırasında bir hata oluştu.');
    }
  };

  const togglePortfoy = (id: number) => {
    setSelectedPortfoyler(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const filteredSunumlar = sunumlar.filter(s => {
    const matchSearch = !searchText ||
      (s.baslik && s.baslik.toLowerCase().includes(searchText.toLowerCase())) ||
      s.cariAdSoyad.toLowerCase().includes(searchText.toLowerCase()) ||
      s.takipNo.toLowerCase().includes(searchText.toLowerCase());
    const matchDurum = filterDurumlar.length === 0 || filterDurumlar.includes(s.durum);
    const matchTarih = !filterTarih || s.olusturmaTarihi.startsWith(filterTarih);
    return matchSearch && matchDurum && matchTarih;
  });

  const filteredCariler = cariler.filter(c =>
    !cariSearch ||
    c.adiSoyadi.toLowerCase().includes(cariSearch.toLowerCase()) ||
    (c.firmaAdi && c.firmaAdi.toLowerCase().includes(cariSearch.toLowerCase())) ||
    c.telefon1.includes(cariSearch)
  );

  const filteredPortfoyler = portfoyler.filter(p =>
    !portfoySearch ||
    p.baslik.toLowerCase().includes(portfoySearch.toLowerCase()) ||
    p.ilanNo.toLowerCase().includes(portfoySearch.toLowerCase()) ||
    (p.adres && p.adres.toLowerCase().includes(portfoySearch.toLowerCase()))
  );

  const fotoUrl = (dosyaYolu: string) => `${axiosInstance.defaults.baseURL}/api/EmlakPortfoy/foto/${dosyaYolu}`;

  const getVitrinFoto = (p: EmlakPortfoyItem): string | null => {
    const vitrin = p.fotograflar?.find(f => f.isVitrin);
    if (vitrin) return vitrin.dosyaYolu;
    if (p.fotograflar?.length > 0) return p.fotograflar[0].dosyaYolu;
    return null;
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div className="es-container">
      {/* Header */}
      <div className="es-header">
        <div className="es-header-left">
          <h1 className="es-title">Sunum ve Randevular</h1>
          <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>Müşteri sunumlarınızı ve planlanan görüşmelerinizi organize edin.</p>
        </div>
        <button className="es-btn-primary" onClick={openCreateModal}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Yeni Sunum
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        {/* Search */}
        <div className="es-filters-search-wrap" style={{ position: 'relative', flex: '0 0 220px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            className="es-search"
            style={{ paddingLeft: 32, height: 36 }}
            placeholder="Sunum veya müşteri ara..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>

        {/* Status pills */}
        <div className="es-filters-durum-wrap" style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', alignItems: 'center', flex: 1 }}>
          {Object.entries(sunumDurumLabels).map(([key, label]) => {
            const active = filterDurumlar.includes(key);
            return (
              <button
                key={key}
                onClick={() => setFilterDurumlar(prev =>
                  prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key]
                )}
                style={{
                  padding: '4px 11px', borderRadius: 9999, fontSize: 12, fontWeight: 500,
                  border: active ? '1px solid #f97316' : '1px solid transparent',
                  background: active ? '#fff7ed' : '#f8fafc',
                  color: active ? '#c2410c' : '#4b5563',
                  boxShadow: active ? '0 0 0 1px #f97316' : 'none',
                  cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap', lineHeight: 1.4
                }}
              >
                {label}
              </button>
            );
          })}
          {filterDurumlar.length > 0 && (
            <button
              onClick={() => setFilterDurumlar([])}
              style={{ fontSize: 12, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', marginLeft: 2 }}
            >
              Temizle
            </button>
          )}
        </div>

        {/* Date picker */}
        <TrDateInput
          value={filterTarih}
          onChange={e => setFilterTarih(e.target.value)}
          style={{
            height: 36, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 8,
            fontSize: 13, color: filterTarih ? '#1e293b' : '#94a3b8', background: '#fff',
            outline: 'none', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto'
          }}
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="es-loading"><i className="fas fa-spinner fa-spin"></i> Yükleniyor...</div>
      ) : filteredSunumlar.length === 0 ? (
        <div className="es-empty">
          <i className="fas fa-folder-open"></i>
          <p>Henüz sunum oluşturulmamış.</p>
        </div>
      ) : (
        <div className="es-list">
          {filteredSunumlar.map(sunum => (
            <div key={sunum.sunumID} className="es-card" onClick={() => navigate(`/emlak-sunumlari/${sunum.sunumID}`)}>
              <div className="es-card-left">
                <div className="es-card-durum-bar" style={{ backgroundColor: sunumDurumBarColor[sunum.durum] || '#94a3b8' }}></div>
                <div className="es-card-info">
                  <div className="es-card-top">
                    <h3 className="es-card-title" style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>
                      {sunum.baslik || sunum.cariAdSoyad + ' Sunumu'}
                    </h3>
                    {(() => {
                      const badge = sunumDurumBadge[sunum.durum] || { bg: '#f1f5f9', color: '#475569' };
                      return (
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 9999,
                          background: badge.bg, color: badge.color, whiteSpace: 'nowrap'
                        }}>
                          {sunumDurumLabels[sunum.durum] || sunum.durum}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="es-card-meta" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 18px', marginTop: 5 }}>
                    {/* Stacked: müşteri + takip no */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>{sunum.cariAdSoyad}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{sunum.takipNo}</span>
                    </div>
                    {sunum.cariTelefon && (
                      <span style={{ fontSize: 12, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{sunum.cariTelefon}</span>
                    )}
                    <span style={{ fontSize: 12, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{formatDate(sunum.olusturmaTarihi)}</span>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{sunum.olusturanAdSoyad}</span>
                  </div>
                </div>
              </div>
              <div className="es-card-right">
                <div className="es-card-portfoy-count">
                  <span className="es-card-portfoy-num">{sunum.portfoyAdet}</span>
                  <span className="es-card-portfoy-label">Portföy</span>
                </div>
                <div className="es-card-actions" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => navigate(`/emlak-sunumlari/${sunum.sunumID}`)}
                    title="Detay"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                      color: '#94a3b8', display: 'flex', alignItems: 'center', borderRadius: 6, transition: 'color 0.15s'
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#cd6906')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(sunum.sunumID)}
                    title="Sil"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                      color: '#94a3b8', display: 'flex', alignItems: 'center', borderRadius: 6, transition: 'color 0.15s'
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (() => {
        const inputSt: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' };
        const labelSt: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 };
        const iFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; };
        const iBlur  = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; };
        return (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            onClick={() => setShowCreateModal(false)}
          >
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }} />
            <div
              style={{ position: 'relative', width: '100%', maxWidth: 672, maxHeight: '90vh', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', borderRadius: 16, overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ flexShrink: 0, padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>Yeni Sunum Oluştur</h2>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>Müşteri ve portföy seçerek yeni bir sunum oluşturun.</p>
                  </div>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', borderRadius: 6, display: 'flex', alignItems: 'center', transition: 'color 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#4b5563')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                  ><X size={20} /></button>
                </div>
              </div>

              {/* Body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Başlık */}
                <div>
                  <label style={{ ...labelSt, justifyContent: 'flex-start', gap: 4 }}>
                    Sunum Başlığı
                    <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(opsiyonel)</span>
                  </label>
                  <input type="text" style={inputSt} placeholder="Ör: Villa gösterimi" value={sunumBaslik} onChange={e => setSunumBaslik(e.target.value)} onFocus={iFocus} onBlur={iBlur} />
                </div>

                {/* Müşteri */}
                <div style={{ position: 'relative' }}>
                  <label style={labelSt}>
                    <span>Müşteri <span style={{ color: '#f43f5e' }}>*</span></span>
                    <button
                      type="button"
                      onClick={handleOpenHizliMusteri}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', cursor: 'pointer', color: '#cd6906', fontSize: 12, fontWeight: 500, padding: '4px 8px', borderRadius: 6, transition: 'background 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(205,105,6,0.08)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    ><UserPlus size={13} /> Hızlı Ekle</button>
                  </label>
                  {selectedCari ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, background: '#f9fafb' }}>
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{selectedCari.adiSoyadi}</span>
                        {selectedCari.firmaAdi && <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 6 }}>— {selectedCari.firmaAdi}</span>}
                        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 6 }}>| {selectedCari.telefon1}</span>
                      </div>
                      <button onClick={() => setSelectedCari(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', padding: 2, borderRadius: 4, transition: 'color 0.15s' }} onMouseEnter={e => (e.currentTarget.style.color = '#374151')} onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}><X size={14} /></button>
                    </div>
                  ) : (
                    <div>
                      <input type="text" style={inputSt} placeholder="Müşteri ara..." value={cariSearch} onChange={e => setCariSearch(e.target.value)} onFocus={iFocus} onBlur={iBlur} />
                      {cariSearch.length > 0 && (
                        <div style={{ marginTop: 4, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
                          {filteredCariler.slice(0, 50).map(cari => (
                            <div
                              key={cari.cariID}
                              onClick={() => { setSelectedCari(cari); setCariSearch(''); }}
                              style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', display: 'flex', flexDirection: 'column', gap: 2, transition: 'background 0.1s' }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                              onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                            >
                              <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{cari.adiSoyadi}{cari.firmaAdi && <span style={{ color: '#6b7280', fontWeight: 400 }}> — {cari.firmaAdi}</span>}</span>
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>{cari.telefon1}</span>
                            </div>
                          ))}
                          {filteredCariler.length === 0 && <div style={{ padding: '12px 16px', fontSize: 13, color: '#9ca3af' }}>Sonuç bulunamadı</div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Portföy */}
                <div>
                  <label style={labelSt}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      Portföyler <span style={{ color: '#f43f5e' }}>*</span>
                      <span style={{ background: '#f3f4f6', color: '#6b7280', fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 500 }}>{selectedPortfoyler.length} seçili</span>
                    </span>
                  </label>
                  <input type="text" style={{ ...inputSt, marginBottom: 8 }} placeholder="Portföy ara (başlık, ilan no, adres)..." value={portfoySearch} onChange={e => setPortfoySearch(e.target.value)} onFocus={iFocus} onBlur={iBlur} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 240, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    {filteredPortfoyler.slice(0, 30).map(p => {
                      const isSelected = selectedPortfoyler.includes(p.portfoyID);
                      const foto = getVitrinFoto(p);
                      return (
                        <div
                          key={p.portfoyID}
                          onClick={() => togglePortfoy(p.portfoyID)}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f9fafb', background: isSelected ? '#fff7ed' : '#fff', transition: 'background 0.1s' }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = isSelected ? '#fff7ed' : '#fff'; }}
                        >
                          <div style={{ width: 48, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0, border: '1px solid #e5e7eb', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {foto ? <img src={fotoUrl(foto)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 18, color: '#d1d5db' }}>🖼</span>}
                          </div>
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.baslik}</div>
                            <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', gap: 8, marginTop: 2 }}>
                              <span>{p.ilanNo}</span>
                              <span>{p.fiyat.toLocaleString('tr-TR')} ₺</span>
                              <span>{p.m2} m²</span>
                            </div>
                          </div>
                          {isSelected && <Check size={16} style={{ color: '#cd6906', flexShrink: 0 }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Açıklama */}
                <div>
                  <label style={{ ...labelSt, justifyContent: 'flex-start', gap: 4 }}>
                    Açıklama
                    <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(opsiyonel)</span>
                  </label>
                  <textarea style={{ ...inputSt, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Sunum hakkında genel not..." value={sunumAciklama} onChange={e => setSunumAciklama(e.target.value)} onFocus={iFocus} onBlur={iBlur} />
                </div>
              </div>

              {/* Footer */}
              <div style={{ flexShrink: 0, padding: '20px', borderTop: '1px solid #e5e7eb', background: '#fff', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{ padding: '10px 20px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                >İptal</button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  style={{ padding: '10px 24px', background: creating ? '#e5a44d' : '#cd6906', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: creating ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'background 0.15s, box-shadow 0.15s' }}
                  onMouseEnter={e => { if (!creating) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; } }}
                  onMouseLeave={e => { if (!creating) { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; } }}
                >
                  {creating ? 'Oluşturuluyor...' : <><Check size={16} /> Oluştur</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Modal */}
      {deleteConfirm && (
        <div className="es-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="es-modal" onClick={e => e.stopPropagation()}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-exclamation-triangle" style={{ color: '#dc2626', marginRight: 8 }}></i>Sunumu Sil</span>
              <button className="es-modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="es-modal-body">
              <p>Bu sunumu silmek istediğinize emin misiniz?</p>
            </div>
            <div className="es-modal-footer">
              <button className="es-btn-cancel" onClick={() => setDeleteConfirm(null)}>İptal</button>
              <button className="es-btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                <i className="fas fa-trash"></i> Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hızlı Müşteri Ekle Modal */}
      {showHizliMusteriModal && (
        <div className="es-modal-overlay" style={{ zIndex: 1100 }} onClick={() => setShowHizliMusteriModal(false)}>
          <div className="es-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="es-modal-header">
              <span className="es-modal-title"><i className="fas fa-user-plus" style={{ color: '#cd6906', marginRight: 8 }}></i>Hızlı Müşteri Ekle</span>
              <button className="es-modal-close" onClick={() => setShowHizliMusteriModal(false)}>✕</button>
            </div>
            <div className="es-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="es-form-group">
                  <label className="es-label">Adı Soyadı *</label>
                  <input className="es-input" type="text" maxLength={100} value={hizliMusteriForm.adiSoyadi}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, adiSoyadi: e.target.value }))} />
                </div>
                <div className="es-form-group">
                  <label className="es-label">Firma Adı</label>
                  <input className="es-input" type="text" maxLength={100} value={hizliMusteriForm.firmaAdi}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, firmaAdi: e.target.value }))} />
                </div>
                <div className="es-form-group">
                  <label className="es-label">Telefon 1 *</label>
                  <input className="es-input" type="text" maxLength={14} placeholder="5XXXXXXXXX" value={hizliMusteriForm.telefon1}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, telefon1: e.target.value }))} />
                </div>
                <div className="es-form-group">
                  <label className="es-label">Telefon 2</label>
                  <input className="es-input" type="text" maxLength={14} placeholder="5XXXXXXXXX" value={hizliMusteriForm.telefon2}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, telefon2: e.target.value }))} />
                </div>
                <div className="es-form-group">
                  <label className="es-label">E-Posta</label>
                  <input className="es-input" type="email" maxLength={100} value={hizliMusteriForm.ePosta}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, ePosta: e.target.value }))} />
                </div>
                <div className="es-form-group">
                  <label className="es-label">TC</label>
                  <input className="es-input" type="text" maxLength={11} value={hizliMusteriForm.tc}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, tc: e.target.value }))} />
                </div>
                <div className="es-form-group">
                  <label className="es-label">Vergi Dairesi</label>
                  <input className="es-input" type="text" maxLength={100} value={hizliMusteriForm.cariVD}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, cariVD: e.target.value }))} />
                </div>
                <div className="es-form-group">
                  <label className="es-label">Vergi No</label>
                  <input className="es-input" type="text" maxLength={50} value={hizliMusteriForm.cariVN}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, cariVN: e.target.value }))} />
                </div>
              </div>
              {hizliMusteriAltKategoriler.length > 0 && (
                <div className="es-form-group" style={{ marginTop: 8 }}>
                  <label className="es-label">Alt Kategori</label>
                  <select className="es-input" value={hizliMusteriForm.turu2}
                    onChange={e => setHizliMusteriForm(p => ({ ...p, turu2: e.target.value }))}>
                    <option value="">Seçiniz...</option>
                    {hizliMusteriAltKategoriler.map(k => (
                      <option key={k.cariAltKategoriID} value={k.altKategoriAdi}>{k.altKategoriAdi}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="es-form-group" style={{ marginTop: 8 }}>
                <label className="es-label">Adres</label>
                <textarea className="es-textarea" rows={2} maxLength={500} value={hizliMusteriForm.adres}
                  onChange={e => setHizliMusteriForm(p => ({ ...p, adres: e.target.value }))} />
              </div>
            </div>
            <div className="es-modal-footer">
              <button className="es-btn-cancel" onClick={() => setShowHizliMusteriModal(false)}>İptal</button>
              <button className="es-btn-primary" onClick={handleHizliMusteriSave} disabled={hizliMusteriSaving}>
                {hizliMusteriSaving ? <><i className="fas fa-spinner fa-spin"></i> Kaydediliyor...</> : <><i className="fas fa-user-plus"></i> Müşteri Ekle</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmlakSunumlari;
